import type { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { useAcquisitionGetProductTariffs } from '@eneco/dc/hooks/sales';
import { getMarketingProductCodes, getProductTariffsProducts, getTariffValues } from '@eneco/dc/modelContract/tariffs';
import useDC from '@eneco/dc/useDC';
import { useFormatter } from '@eneco/i18n';
import { Text, Table as SparkyTable, Stack, Stretch, Skeleton } from '@eneco/sparky';
import { TableRendering } from '@sitecore/types/manual/Table';

// Fixed costs (e.g. `{vasteLeveringskostenGasPerMaandVast}`) are monthly prices, everything else is a per-unit tariff.
const isFixedCostPlaceholder = (placeholder: string) => placeholder.replace(/^{/, '').startsWith('vaste');

const ModelContractTable: FC<TableRendering> = ({ fields }) => {
  const { label } = useDC();
  const { currency } = useFormatter();

  const codes = getMarketingProductCodes(label);
  const { data, isLoading } = useAcquisitionGetProductTariffs(
    codes ? { products: getProductTariffsProducts(codes) } : null,
  );

  const tariffs = getTariffValues(codes, data?.productTariffs);

  if (isLoading) {
    return (
      <Stack direction="row" gap="2">
        <Stretch>
          <Skeleton height="480px" />
        </Stretch>
      </Stack>
    );
  }

  return (
    <SparkyTable title={fields.title?.value}>
      {fields.headerRows?.length && (
        <SparkyTable.Header>
          {fields.headerRows[0].columns.map((column, index) => (
            <Text key={`${index}-${column.content.value}`}>{column.content.value}</Text>
          ))}
        </SparkyTable.Header>
      )}

      {fields.rows?.length &&
        fields.rows.map((row, rowIndex) => (
          <SparkyTable.Row key={`row-${rowIndex}`}>
            {row.columns.map((column, columnIndex) => {
              if (columnIndex === 0) {
                return (
                  <RichText key={`${rowIndex}-${columnIndex}-${column.content.value}`} html={column.content.value} />
                );
              }
              // dynamic values, read placeholder from Sitecore then get the corresponding value from the dictionary.
              const placeholder = column.content.value;
              const tariff = tariffs[placeholder];
              const formatTariff = isFixedCostPlaceholder(placeholder) ? currency.euro : currency.euroFiveDecimals;
              return (
                <RichText
                  key={`${rowIndex}-${columnIndex}-${placeholder}`}
                  html={tariff === undefined ? '--' : formatTariff(tariff)}
                />
              );
            })}
          </SparkyTable.Row>
        ))}
    </SparkyTable>
  );
};

export { ModelContractTable };
export default ModelContractTable;
